import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ScopedCssBaseline,
  Stack,
  Typography,
} from '@mui/material';
import { endOfMonth, startOfMonth } from 'date-fns';
import { useState } from 'react';

import type { CollapseInfo } from '../VivaRessourcenCalendars.utils.types';

import { ThemaFromThemaRouter, ZeitlicherAblauf, Zeitraum } from '../../../../dtos';
import { isPcCenterThema } from '../../../../utils';
import { ContentLoading } from '../../ContentLoading';
import { CalendarEvent, FilterParameters, ResourceCalendar, ResourceCalendarFilters } from '../../ResourceCalendar';
import { SuggestedEvents } from '../../ResourceCalendar/helperClasses/CalendarResource/AbstractCalendarResource';
import { View, views } from '../../ResourceCalendar/ResourceCalendar';
import { useResourcesAndEvents } from '../useResourcesAndEvents';
import {
  handleExpansionChange,
  onCalendarCellClicked,
  onCalendarEventClicked,
  onCalendarTimeRangeChanged,
  onEventUpdate,
  onEventUpdated,
  onViewChange,
} from '../VivaRessourcenCalendars.logic';
import { createCalendarColorsWithSelectionBounds, groupCalendarResources } from '../VivaRessourcenCalendars.utils';
import { content } from './DurchgangRessourcenCalendar.content';

export type DurchgangRessourcenCalendarProps = {
  readonly thema: Pick<ThemaFromThemaRouter, 'akaThemaId' | 'planungsinformation' | 'isOnline' | 'planungsformat'>;
  readonly initDate: Date;
  readonly suggestedEvents: SuggestedEvents;
  readonly zeitlicherAblauf: ZeitlicherAblauf;
  readonly givenFilterParams: FilterParameters;
  readonly isOnlineModul: boolean;
  readonly gruppenraumInfo: string | null;
  readonly modulTage: number;
  readonly open: boolean;
  readonly onAbort: () => void;
  readonly onApply: (calendarEvents: readonly CalendarEvent[], ablauf: Zeitraum[]) => void;
};

const dialogWidth = 'calc(100vw - 96px)';
const dialogContentWidth = `calc(${dialogWidth} - 48px)`;

export const DurchgangRessourcenCalendar = ({
  thema,
  initDate,
  suggestedEvents,
  zeitlicherAblauf,
  givenFilterParams,
  isOnlineModul,
  gruppenraumInfo,
  modulTage,
  open,
  onAbort,
  onApply,
}: DurchgangRessourcenCalendarProps) => {
  const needsGruppenraum = gruppenraumInfo !== null;

  const [collapseInfos, setCollapseInfos] = useState<CollapseInfo>({
    seminarraum: isOnlineModul,
    gruppenraum: true,
    standort: true,
    experte: false,
    haupttrainer: false,
    nebentrainer: false,
    ersatztrainer: false,
  });

  const [filterParams, setFilterParams] = useState<FilterParameters>({ ...givenFilterParams, freieTage: modulTage });

  if (!thema.planungsinformation) {
    throw new Error('thema.planungsinformation is null');
  }

  const [timeRange, setTimeRange] = useState(() => ({
    start: startOfMonth(initDate),
    end: endOfMonth(initDate),
  }));
  const [view, setView] = useState<View>(views.WEEK);
  const [ablauf, setAblauf] = useState(suggestedEvents.tage.map((e) => e.zeitraum));

  const {
    seminarraumResources,
    gruppenraumResources,
    standortResources,
    experteResources,
    fixedEvents,
    pendingEvents,
    setPendingEvents,
  } = useResourcesAndEvents({
    akaThemaId: thema.akaThemaId,
    planungsinformation: thema.planungsinformation,
    skipTrainerGueltigkeitsCheck: true,
    akaVeranstaltungId: null,
    filterParams,
    timeRange,
    view,
    suggestedEvents,
  });

  const fixedAndPendingEvents = [...fixedEvents, ...pendingEvents];

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          sx: {
            p: 1,
            width: dialogWidth,
            maxWidth: 'unset',
            borderRadius: '16px',
            zIndex: (theme) => theme.zIndex.modal + 1,
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="headingXxs">{content.title}</Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => onApply(pendingEvents, ablauf)} variant="contained">
              {content.apply}
            </Button>
            <IconButton size="small" sx={{ border: '1px solid lightgrey', borderRadius: '50%' }} onClick={onAbort}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
      </DialogTitle>

      <DialogContent sx={{ width: dialogContentWidth, pb: 3, px: 3 }}>
        <ScopedCssBaseline>
          <Box display="flex" flexDirection="column">
            {!isOnlineModul && (
              <ResourceCalendarFilters
                filterParameters={filterParams}
                // eslint-disable-next-line @eslint-react/purity -- added during migration to ban-pdk. please solve issue or explain disable-reason here
                startYear={thema.planungsinformation.planungszeitraum?.start.getFullYear() ?? new Date().getFullYear()}
                onFilterChange={setFilterParams}
                padding={0}
              />
            )}
            {!fixedAndPendingEvents.length && <ContentLoading />}
            {/*
              This height is important, the calendar needs to be contained in a container with a height, see
              https://mobiscroll.com/docs/react/eventcalendar/api#opt-view.

              Without this height, https://jira.haufe.io/browse/AKAVM-2370 will occur again.
            */}
            {fixedAndPendingEvents.length > 0 && (
              <div className="h-[100vh-300px]">
                <ResourceCalendar
                  initView={view}
                  resources={groupCalendarResources({
                    seminarraumResources,
                    gruppenraumResources,
                    standortResources,
                    experteResources,
                    collapseInfo: collapseInfos,
                    planungsinformation: thema.planungsinformation,
                    isOnlineVeranstaltung: isOnlineModul,
                    includeGruppenraeume: needsGruppenraum,
                    isModultermin: true,
                    events: fixedAndPendingEvents,
                  })}
                  events={fixedAndPendingEvents}
                  colors={createCalendarColorsWithSelectionBounds(
                    seminarraumResources,
                    gruppenraumResources,
                    standortResources,
                    experteResources,
                    fixedAndPendingEvents,
                    ablauf,
                    timeRange,
                  )}
                  timeRange={timeRange}
                  initialDate={initDate}
                  timeRangeChangeHandler={(newTimeRange) => onCalendarTimeRangeChanged(newTimeRange, setTimeRange)}
                  toggleExpansion={(resourceType, expanded) =>
                    handleExpansionChange(resourceType, expanded, setCollapseInfos)
                  }
                  onViewChange={(_oldView, newView) => onViewChange(_oldView, newView, setPendingEvents, setView)}
                  cellClickHandler={(cellClickEvent) =>
                    onCalendarCellClicked({
                      args: cellClickEvent,
                      ablauf,
                      pendingEvents,
                      isPcCenterThema: isPcCenterThema(thema.planungsinformation),
                      zeitlicherAblauf,
                      istTerminreihe: false,
                      fixedEvents,
                      seminarraumResources,
                      gruppenraumResources,
                      standortResources,
                      experteResources,
                      view,
                      setAblauf,
                      setPendingEvents,
                    })
                  }
                  eventClickHandler={(eventClickEvent) =>
                    onCalendarEventClicked(eventClickEvent, setPendingEvents, setAblauf, zeitlicherAblauf, ablauf)
                  }
                  onEventUpdate={(updateEvent) => onEventUpdate(updateEvent, ablauf, false, view, pendingEvents)}
                  onEventUpdated={(updatedEvent) =>
                    onEventUpdated({
                      updateEvent: updatedEvent,
                      pendingEvents,
                      ablauf,
                      setAblauf,
                      zeitlicherAblauf,
                      isPcCenterThema: isPcCenterThema(thema.planungsinformation),
                      istTerminreihe: false,
                      fixedEvents,
                      seminarraumResources,
                      experteResources,
                      setPendingEvents,
                    })
                  }
                />
              </div>
            )}
          </Box>
        </ScopedCssBaseline>
      </DialogContent>
    </Dialog>
  );
};
